import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';

import type { AuthTenant, AuthUser, LoginResponse } from '../lib/types';

/**
 * Auth store — Zustand persisted em localStorage canonical (v1 protótipo).
 *
 * Decisão Fatia 2 original: NÃO persistir. Refresh = relogin. Aceitável
 * canonical em protótipo precoce.
 *
 * Revisão Wave canonical (sessão testes ao vivo 19-20/Mai/2026): refresh
 * frequente durante validação canonical interrompia workflow. Adotado
 * `persist` middleware Zustand com `localStorage` canonical para destravar
 * UX de teste. Produção canonical: migrar para cookie httpOnly emitido
 * pelo Engine (Sprint Backend Security futura) com Refresh canonical
 * automático antes do JWT expirar.
 *
 * Storage key: `lume:auth` canonical (versionado v1 para migration futura).
 * Não persiste `isHydrating` (estado UI transiente canonical).
 */

export interface AuthState {
  accessToken: string | null;
  refreshToken: string | null;
  user: AuthUser | null;
  tenant: AuthTenant | null;
  capabilities: string[];
  isHydrating: boolean;

  setSession: (payload: LoginResponse) => void;
  setMe: (params: { user: AuthUser; tenant: AuthTenant; capabilities: string[] }) => void;
  clear: () => void;
  setHydrating: (v: boolean) => void;
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set) => ({
      accessToken: null,
      refreshToken: null,
      user: null,
      tenant: null,
      capabilities: [],
      isHydrating: false,

      setSession: (payload) =>
        set({
          accessToken: payload.accessToken,
          refreshToken: payload.refreshToken,
        }),

      setMe: ({ user, tenant, capabilities }) => set({ user, tenant, capabilities }),

      clear: () =>
        set({
          accessToken: null,
          refreshToken: null,
          user: null,
          tenant: null,
          capabilities: [],
          isHydrating: false,
        }),

      setHydrating: (v) => set({ isHydrating: v }),
    }),
    {
      name: 'lume:auth',
      storage: createJSONStorage(() => localStorage),
      partialize: (state) => ({
        accessToken: state.accessToken,
        refreshToken: state.refreshToken,
        user: state.user,
        tenant: state.tenant,
        capabilities: state.capabilities,
      }),
    },
  ),
);
